import React from "react";
import { useState } from "react";
import { Link, navigate } from "@reach/router";
import { createAccount } from "../../auth/createAccount";
import { validate as isEmail } from "isemail";
import { makeStyles } from "@material-ui/core/styles";
import {
  TextField,
  Button,
  InputAdornment,
  IconButton,
  Divider,
  Typography,
} from "@material-ui/core/";
import { MdArrowBack } from "react-icons/md";
import BackgroundWrapper from "../base/BackgroundWrapper";
import { BsEyeFill } from "react-icons/bs";
import { BsEyeSlashFill } from "react-icons/bs";
import Div100vh from "@rodmg/react-div-100vh";

const useStyles = makeStyles((theme) => ({
  wrapper: {
    height: "100%",
    padding: theme.spacing(2.5),
    boxSizing: "border-box",
    display: "flex",
    alignItems: "stretch",
  },
  contentContainer: {
    zIndex: "1",
    display: "flex",
    height: "70%",
    width: "100%",
    flexDirection: "column",
    justifyContent: "center",
  },
  link: {
    color: theme.palette.grey[100],
  },
  // TextField
  root: {
    "& label.Mui-focused": {
      color: theme.palette.grey[100],
    },
    "& .MuiInput-underline:before": {
      borderBottomColor: theme.palette.grey[500],
    },
    "& .MuiInput-underline:after": {
      borderBottomColor: theme.palette.grey[100],
    },
    "& .MuiInput-input": {
      color: theme.palette.grey[100],
    },
    "& .MuiInputBase-root": {
      color: theme.palette.grey[100],
    },
    "& .MuiFormLabel-root": {
      color: theme.palette.grey[100],
    },
    marginBottom: theme.spacing(2),
  },
  button: {
    marginTop: theme.spacing(2),
  },
  errorMessage: {
    zIndex: "1",
    marginBottom: theme.spacing(2),
    color: theme.palette.error.main,
  },
  icon: {
    color: theme.palette.common.white,
  },
  linkContainer: {
    position: "absolute",
    width: "100%",
    bottom: "0",
  },
  linkBottom: {
    position: "relative",
    bottom: theme.spacing(10),
  },
  linkWhite: {
    color: theme.palette.common.white,
  },
  divider: {
    background: theme.palette.grey[600],
    margin: theme.spacing(4, 10, 1.5, 10),
  },
}));

function CreateAccount() {
  const classes = useStyles();
  const [emailValue, setEmailValue] = useState("");
  const [passwordValue, setPasswordValue] = useState("");
  const [usernameValue, setUsernameValue] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const onCreateAccountClicked = async (e) => {
    e.preventDefault();
    setErrorMessage("");
    const validationError = validateForm();
    if (validationError) {
      setErrorMessage(validationError);
      return;
    }

    try {
      const isSuccessful = await createAccount(usernameValue, emailValue, passwordValue);
      isSuccessful && navigate(`/sign-in`, { state: { isSuccessful } });
    } catch (e) {
      switch (e.message) {
        case "auth/email-already-in-use":
          setErrorMessage("Die angegebene E-Mail Adresse wird bereits verwendet.");
          break;
        case "auth/weak-password":
          setErrorMessage("Dein Passwort muss mindestens 6 Zeichen haben.");
          break;
        default:
          setErrorMessage(
            "Leider ist ein Fehler aufgetreten. Bitte überprüfe deine Eingabe oder probiere es später noch einmal."
          );
      }
    }
  };

  const validateForm = () => {
    if (!usernameValue) return "Bitte gib einen Nutzernamen ein.";
    if (usernameValue.length < 3) return "Bitte gib mindestens 3 Buchstaben als Nutzername an.";
    if (!emailValue) return "Bitte gib E-Mail Adresse ein.";
    if (!isEmail(emailValue)) return "Bitte gib eine gültige E-Mail Adresse an.";
    if (passwordValue !== confirmPassword) return "Die Passwörter stimmen nicht überein.";
    return null;
  };

  return (
    <BackgroundWrapper blurValue={4}>
      <Div100vh>
        <div className={classes.wrapper}>
          <div>
            <Link to="/" className={classes.link}>
              <MdArrowBack size="2.5em" style={{ transform: "translate(-25%)" }} />
            </Link>
            <div className={classes.contentContainer}>
              {errorMessage && <div className={classes.errorMessage}> {errorMessage} </div>}
              <form noValidate autoComplete="off">
                <TextField
                  inputProps={{
                    autoComplete: "given-name",
                  }}
                  type="text"
                  label="Nutzername"
                  fullWidth
                  className={classes.root}
                  onChange={(e) => setUsernameValue(e.target.value)}
                />
                <TextField
                  inputProps={{
                    autoComplete: "email",
                  }}
                  type="email"
                  label="E-Mail"
                  fullWidth
                  className={classes.root}
                  onChange={(e) => setEmailValue(e.target.value)}
                />
                <TextField
                  type={showPassword ? "text" : "password"}
                  label="Passwort"
                  fullWidth
                  className={classes.root}
                  onChange={(e) => setPasswordValue(e.target.value)}
                  InputProps={{
                    autoComplete: "new-password",
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={() => setShowPassword((showPassword) => !showPassword)}
                        >
                          {showPassword ? <BsEyeFill /> : <BsEyeSlashFill />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                <TextField
                  type={showPassword ? "text" : "password"}
                  label="Passwort wiederholen"
                  fullWidth
                  className={classes.root}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  InputProps={{
                    autoComplete: "new-password",
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={() => setShowPassword((showPassword) => !showPassword)}
                        >
                          {showPassword ? (
                            <BsEyeFill className={classes.icon} />
                          ) : (
                            <BsEyeSlashFill className={classes.icon} />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                <Button
                  variant="contained"
                  color="primary"
                  fullWidth={true}
                  className={classes.button}
                  onClick={onCreateAccountClicked}
                  type="submit"
                >
                  REGISTRIEREN
                </Button>
              </form>
            </div>
          </div>
        </div>
        <div className={classes.linkContainer}>
          <div align="center" className={classes.linkBottom}>
            <Divider className={classes.divider} />
            <Link to="/sign-in" className={classes.linkWhite}>
              <Typography variant="subtitle2" style={{ maxWidth: "85vw" }}>
                Du hast bereits einen Account? ANMELDEN
              </Typography>
            </Link>
          </div>
        </div>
      </Div100vh>
    </BackgroundWrapper>
  );
}

export default CreateAccount;
